import { type ReactNode, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type DriftedTasksView, type TaskView } from '@apm/contracts';
import {
  Bot,
  Columns3,
  GanttChartSquare,
  List,
  MessageSquareText,
  Pencil,
  Plus,
  Repeat,
  Scale,
  Sparkles,
  Wand2,
  Waypoints,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Dialog } from '../../../components/ui/dialog';
import { EmptyState, Skeleton } from '../../../components/ui/states';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { cn } from '../../../lib/utils';
import { BULK_EDIT_HOVER, GANTT_EDIT_HOVER, NEW_TASK_HOVER } from './toolbar-button-hover';
import { Board } from '../../board/components/board';
import { boardApi, boardKeys } from '../../board/api';
import { ganttApi, ganttKeys } from '../../gantt/api';
import { GanttPanel } from '../../gantt/components/gantt-panel';
import { InferDependenciesDialog } from '../../gantt/components/infer-dependencies-dialog';
import { RebalanceAiSection } from './rebalance-ai-section';
import { useReschedule } from '../../gantt/use-reschedule';
import { errorMessage } from '../../../lib/error-message';
import { apiFetch } from '../../../lib/http';
import { isScopeCreep, scheduleFromDrop } from '../schedule-drop';
import { ScheduleDrawer } from './schedule-drawer';
import { defaultSprint } from '../../sprints/default-sprint';
import { ALL_SPRINTS, SprintPicker } from './sprint-picker';
import { DriftBanner, openLateTasks } from './drift-banner';
import { PeriodPicker } from './period-picker';
import { BoardFilterBar } from '../../board/components/board-filter-bar';
import { type BoardFilter, NO_BOARD_FILTER } from '../../board/filter';
import { TaskFormDialog } from './task-form-dialog';
import { PlanDraftsDialog } from '../../plan/components/plan-drafts-dialog';
import { BulkEditDialog } from './bulk-edit-dialog';
import { useAiKeyGate } from '../../ai/components/ai-key-required';
import { useAiRunConfirm } from '../../ai/components/ai-run-confirm';
import { TaskListView } from './task-list-view';
import { TaskPopup } from './task-popup';
import { RecurrencePanel } from './recurrence-panel';
import { displayDateRange } from '../../../lib/date';

/**
 * Ba cách nhìn của cùng một tập cột và task — APM-BOARD-b. Cách nhìn thứ tư,
 * `recurring` (APM-REC-b), chỉ có ở dự án bảo trì: nó là danh sách **mẫu** việc
 * định kỳ, không phải một cách vẽ khác của task.
 *
 * ⚠️ Tên là `TasksViewMode`, không phải `TaskView`: `TaskView` là **một task**
 * trong `@apm/contracts`. Trùng tên thì file nào import cả hai sẽ nhận một lỗi
 * biên dịch khó đọc, và tệ hơn là chỗ nào chỉ import một cái sẽ đọc nhầm.
 */
export const TASK_VIEWS = ['board', 'gantt', 'list', 'recurring'] as const;
export type TasksViewMode = (typeof TASK_VIEWS)[number];

const ICONS = {
  board: Columns3,
  gantt: GanttChartSquare,
  list: List,
  recurring: Repeat,
} as const;

/**
 * Nút trên hàng công cụ chỉ còn icon ở điện thoại — nhãn bọc trong
 * `<span className="max-sm:sr-only">`, nên nút thành ô tròn 32px.
 */
const ICON_ONLY_ON_PHONE = 'max-sm:w-8 max-sm:px-0';

/**
 * Tab "Công việc" — một tab, ba cách nhìn (APM-BOARD-b).
 *
 * ## Vì sao gộp Board và Gantt
 *
 * Chúng không phải hai tính năng: chúng là hai cách vẽ **cùng một** tập cột và
 * task. Để rời nhau thì mỗi lần đổi cột hay đổi sprint phải nhớ làm ở hai chỗ,
 * và chúng sẽ lệch — đúng loại lệch không ai thấy cho tới lúc hai màn hình nói
 * hai chuyện khác nhau về cùng một dự án.
 *
 * ## Sprint đang xem sống ở URL, không ở đây
 *
 * Cả ba cách nhìn đọc cùng một `sprintId` từ search param. Giữ nó trong state
 * của tab thì đổi cách nhìn là mất sprint đang xem; giữ mỗi cách nhìn một bản
 * thì chúng lệch nhau ngay lần chuyển thứ hai.
 *
 * ## ⚠️ Bộ chọn sprint hiện ở hai chỗ, và đó là chủ đích
 *
 * Ở Board nó nằm trên đầu **khung sprint**, vì khung là thứ nó điều khiển. Ở
 * Gantt và Danh sách không có khung, nên nó lên thanh công cụ. Cùng một
 * component, cùng một giá trị.
 */
export function TasksPanel({
  projectId,
  view: askedView,
  onViewChange,
  sprintId,
  onSprintChange,
  waterfall,
  maintenance,
  period,
  onPeriodChange,
  deadline,
  canEditColumns,
  canManageColumns = false,
  canEditTasks,
  canAssign = false,
  canApproveSuggestions,
  openedTaskId,
  onOpenedTaskChange,
}: {
  readonly projectId: string;
  readonly view: TasksViewMode;
  readonly onViewChange: (view: TasksViewMode) => void;
  readonly sprintId: string | undefined;
  readonly onSprintChange: (sprintId: string | null) => void;
  readonly waterfall: boolean;
  /**
   * Dự án bảo trì — APM-MAINT-e.
   *
   * Nó không có sprint lẫn ngày kết thúc, nên chia theo **kỳ lịch** thay vì
   * theo sprint. Xem `PeriodPicker` để biết vì sao.
   */
  readonly maintenance: boolean;
  /** Kỳ đang xem, từ URL. Bỏ trống = kỳ chứa hôm nay. */
  readonly period: string | undefined;
  readonly onPeriodChange: (period: string) => void;
  readonly deadline: string | null;
  readonly canEditColumns: boolean;
  /** `workflow:manage` — menu ••• và nút thêm cột trên board. */
  readonly canManageColumns?: boolean;
  readonly canEditTasks: boolean;
  /** `task:assign` — mở ô "Người làm". Xem `canAssign` ở `routes/_app/projects.tsx`. */
  readonly canAssign?: boolean;
  readonly canApproveSuggestions: boolean;
  readonly openedTaskId: string | null;
  readonly onOpenedTaskChange: (taskId: string | null) => void;
}): ReactNode {
  const { t } = useTranslation();
  /**
   * "Định kỳ" chỉ có ở dự án bảo trì — người dùng chốt 2026-09-25. Một đường
   * dẫn `?taskView=recurring` chép từ dự án bảo trì sang dự án khác rơi về
   * Board, thay vì mở một cách nhìn mà thanh chuyển không có.
   */
  const view: TasksViewMode = askedView === 'recurring' && !maintenance ? 'board' : askedView;
  /**
   * Nút tạo việc sống ở **thanh công cụ**, không ở trong Board.
   *
   * Tạo một việc không phải thao tác của riêng cách nhìn nào: đang xem Gantt
   * mà nghĩ ra một việc thì phải tạo được ngay, chứ không phải quay về Board
   * rồi tìm lại chỗ cũ. Đặt ở đây thì cả ba cách nhìn dùng chung một nút.
   *
   * `statusId` để trống ⇒ cột mặc định của dự án. Nút "+" trên từng cột của
   * Board vẫn giữ ngữ cảnh cột của nó — hai đường khác nhau cho hai ý định.
   */
  const [creating, setCreating] = useState(false);
  const [bulkEditing, setBulkEditing] = useState(false);
  /** Hộp "Vì sao nên cân bằng lại như vậy" — APM-PSET-l. */
  const [rebalancing, setRebalancing] = useState(false);

  /*
    Việc đang lệch tiến độ — **cùng khoá** với `DriftBanner` ngay bên dưới.

    React Query gộp hai chỗ đọc cùng khoá thành một lượt gọi, nên đây không
    phải một truy vấn nữa; nó chỉ là chỗ thanh công cụ hỏi "có gì để cân bằng
    không" để quyết định hiện nút. Sao chép khoá thì hai chỗ lệch nhau ở lần ai
    đó đổi endpoint — nên khoá viết y nguyên, và `enabled` cũng cùng điều kiện
    quyền (`task:assign`).
  */
  const drifted = useQuery({
    queryKey: ['rebalance', 'drift', projectId],
    queryFn: () => apiFetch<DriftedTasksView>(`/projects/${projectId}/rebalance/drift`),
    enabled: canAssign,
  });
  // Cùng bộ lọc với `DriftBanner`, để nút "Cân bằng" hiện đúng khi dải hiện.
  const driftedTasks = openLateTasks(drifted.data);
  /**
   * "Suy ra phụ thuộc còn thiếu" — dọn khỏi chân trang Gantt, nay là một mục
   * trong popup "Sửa nhanh" cạnh `bulkEditing` — APM-UX-ai6.
   */
  const [inferDepsOpen, setInferDepsOpen] = useState(false);
  /**
   * Kế hoạch nháp — trước là nút riêng `PlanDraftsButton`, nay là mục trong
   * popup "Sửa nhanh" — APM-UX-ai6. Đường làm tay của nó (vốn là lý do nút
   * riêng còn đứng khi chưa bật AI) nay nằm trong hộp "Cần key AI".
   */
  const [planDraftsOpen, setPlanDraftsOpen] = useState(false);
  /**
   * Menu "Sửa nhanh" **luôn** hiện — người dùng yêu cầu 2026-09-25. Chưa có key
   * thì ba mục AI mở hộp "Cần key AI" (`useAiKeyGate`) thay vì chạy; mục "Xử lý
   * task chậm tiến độ" vẫn mở như thường vì hộp ấy có phần không cần AI.
   */
  const keyGate = useAiKeyGate();
  /** Chế độ sửa biểu đồ Gantt — APM-GANTT-j. Mặc định tắt: biểu đồ để đọc. */
  const [ganttEditing, setGanttEditing] = useState(false);

  /**
   * Bộ lọc/làm nổi dùng chung cho **cả ba** cách nhìn — APM-BOARD-h.
   *
   * Trước đây Board có thanh lọc riêng, còn Danh sách có một bộ lọc khác hẳn
   * (chữ, người, trạng thái, quá hạn) và Gantt thì không có gì. Nên "chỉ xem
   * việc của Bảo" phải đặt lại từ đầu mỗi lần đổi cách nhìn — và đặt bằng một
   * giao diện khác, với một tập tiêu chí khác.
   *
   * Nay một state, một thanh điều khiển, ba cách vẽ. State ở đây vì đây là tổ
   * tiên chung của cả ba — cùng lý do với `sprintId` và `activePeriod`.
   */
  const [filter, setFilter] = useState<BoardFilter>(NO_BOARD_FILTER);

  /**
   * Lượt "Xếp lịch bằng AI" — APM-1313b, giữ ở đây vì nút và bảng kết quả nằm
   * ở hai cây component khác nhau (thanh công cụ ↔ dưới Gantt).
   */
  const reschedule = useReschedule(projectId);
  const aiConfirm = useAiRunConfirm(projectId);
  /**
   * Cùng query với `GanttPanel` nên React Query gộp làm một — không thêm
   * request nào, chỉ đọc lại cái đã có để biết có gì đáng xếp không.
   */
  const impact = useQuery({
    queryKey: ganttKeys.impact(projectId),
    queryFn: () => ganttApi.impact(projectId),
  });
  const worthRescheduling =
    (impact.data?.tasks.length ?? 0) > 0 || (impact.data?.slipDays ?? 0) > 0;

  /**
   * Danh sách sprint cho bộ chọn ngoài Board.
   *
   * Dùng chung `queryKey` với Board nên nó không tốn thêm request nào — React
   * Query trả lại đúng cache ấy.
   */
  const backlog = useQuery({
    queryKey: [...boardKeys.backlog(projectId), 'metadata'],
    queryFn: () => boardApi.backlog(projectId, null, true),
    enabled: !waterfall,
  });

  /**
   * Sprint **đã giải xong**, không phải tham số thô.
   *
   * ⚠️ `sprintId` bỏ trống nghĩa là "sprint đang chạy", và server tự chọn nó.
   * Cho bộ chọn đọc thẳng tham số thì nó hiện "Không theo sprint" trong khi
   * bảng bên dưới **đang** lọc theo Sprint 2 — nhãn nói một đằng, dữ liệu một
   * nẻo, và người dùng tin cái nhãn.
   *
   * Cùng `queryKey` với Board và Danh sách nên không tốn thêm request nào.
   */
  /**
   * "Cả dự án" — người dùng chọn tường minh ở bộ chọn sprint (APM-GANTT-q).
   *
   * Khác hẳn "chưa chọn" (`sprintId === undefined`), thứ khiến server tự lấy
   * sprint đang chạy. Dự án Waterfall thì luôn ở chế độ này: nó không có sprint
   * nào để mà lọc.
   *
   * ⚠️ Board **không** nhận chế độ này (`SprintPicker` không mời chọn ở đó),
   * nhưng người dùng có thể chọn ở Gantt rồi đổi sang Board. Lúc ấy nó rơi về
   * "chưa chọn" và server tự lấy sprint đang chạy — chứ không dựng một board
   * gộp cột của mọi sprint, thứ không đọc được.
   */
  /*
    Kỳ hiệu lực do `projects.tsx` tính — **một** chỗ cho cả tab Công việc lẫn
    tab Báo cáo. Tính riêng ở đây thì hai tab rơi về hai mặc định khác nhau và
    cùng một dự án nói về hai khoảng thời gian.
  */
  const activePeriod = maintenance ? (period ?? null) : null;

  /**
   * Dự án **không có sprint nào** — APM-BOARD-j.
   *
   * ⚠️ Waterfall và bảo trì phải đi cùng nhau ở đây, và bỏ sót bảo trì là một
   * lỗi đã cắn thật: `Board` chỉ dựng cột Backlog khi `all === false`, vì ở dự
   * án không có sprint thì **mọi** task đều "chưa vào sprint" và cột ấy nhân
   * đôi cả board.
   *
   * Hệ quả không phải là hai bản sao trông thấy được — cột Backlog nằm ngoài
   * khung nên nhìn vẫn hợp lý. Nó là: `columnOf` tra vị trí cũ của thẻ và tìm
   * thấy nó ở **Backlog trước** (cột ảo đứng đầu danh sách), nên mọi cú kéo
   * đều bị hiểu là "kéo ra khỏi Backlog" — thao tác đòi phải có một sprint để
   * đưa vào. Không có sprint ⇒ cảnh báo và bỏ qua, **không một lời gọi mạng
   * nào**. Thẻ bật về chỗ cũ và không gì nói rõ vì sao.
   */
  const noSprints = waterfall || maintenance;

  const viewAll = noSprints || (sprintId === ALL_SPRINTS && view !== 'board');
  /** `sprintId` gửi lên server: bỏ sentinel đi, nó không phải id của gì cả. */
  const askedSprintId = sprintId === ALL_SPRINTS ? undefined : sprintId;

  const board = useQuery({
    queryKey: [
      ...boardKeys.board(projectId, viewAll ? ALL_SPRINTS : askedSprintId, activePeriod),
      'metadata',
    ],
    queryFn: () =>
      boardApi.board(projectId, viewAll ? undefined : askedSprintId, viewAll, activePeriod, true),
  });
  const resolvedSprint = viewAll ? null : (board.data?.sprint ?? null);
  const resolvedSprintId = resolvedSprint?.id ?? null;

  /**
   * Dự án Agile có sprint, nhưng chưa sprint nào được chọn hay đang chạy.
   *
   * Chờ `board.data` mới kết luận: trong lúc tải thì `resolvedSprint` là `null`
   * và câu "hãy chọn một sprint" sẽ nhoáng lên rồi biến mất ở **mọi** lượt mở
   * tab — đọc như một lỗi.
   */
  const needsSprint =
    !viewAll &&
    board.data !== undefined &&
    resolvedSprintId === null &&
    (backlog.data?.sprints.length ?? 0) > 0;

  /**
   * Chưa chọn gì thì **tự mở** sprint mặc định — APM-SPRINT-f.
   *
   * Server chỉ tự tìm sprint `active`. Dự án vừa lập kế hoạch xong chưa bắt đầu
   * sprint nào, dự án vừa đóng sprint cuối cũng vậy: cả hai nhận `sprint: null`
   * và màn hình dừng ở câu "Chọn một sprint để xem công việc" — một câu hỏi chỉ
   * có một đáp án hợp lý, hỏi ở đúng lúc người ta mở tab để xem việc.
   *
   * ⚠️ `fallback.id !== sprintId` là thứ chặn vòng lặp. Nếu server trả
   * `sprint: null` cho **chính** id vừa đặt (sprint của dự án khác còn sót ở
   * URL, hoặc sprint vừa bị xoá) thì `needsSprint` lại bật, và thiếu phép so
   * này hiệu ứng sẽ đặt lại đúng id ấy mãi mãi.
   */
  const fallbackSprint = needsSprint ? defaultSprint(backlog.data?.sprints ?? []) : null;
  /** Đang trên đường tự chọn — một khoảnh khắc, nhưng nó phải vẽ ra thứ khác. */
  const autoPicking = fallbackSprint !== null && fallbackSprint.id !== sprintId;

  useEffect(() => {
    if (autoPicking && fallbackSprint) onSprintChange(fallbackSprint.id);
  }, [autoPicking, fallbackSprint, onSprintChange]);

  const queryClient = useQueryClient();

  /**
   * Xếp một việc vào sprint **và** đặt ngày — APM-BOARD-c.
   *
   * ## ⚠️ Hai thay đổi, hai lời gọi, hai event
   *
   * Vào sprint ghi event trên aggregate `sprint` (đường scope của burnup đọc
   * chính nó); đặt ngày ghi event trên `task`. Gộp thành một endpoint tự chế sẽ
   * đổi state của hai aggregate mà chỉ ghi event cho một — cạm bẫy CLAUDE.md
   * ghi là "đã cắn hai lần".
   *
   * Vào sprint **trước**, đặt ngày sau: ngược lại thì có một khoảnh khắc việc
   * đã có ngày mà chưa thuộc sprint nào, và Gantt vẽ ra một thanh ở chỗ nó
   * chưa đáng nằm.
   */
  const schedule = useMutation({
    mutationFn: async ({ task, startDate }: { task: TaskView; startDate: string }) => {
      const range = scheduleFromDrop(task, startDate);
      if (resolvedSprintId !== null) await boardApi.moveToSprint(task.id, resolvedSprintId);
      await ganttApi.reschedule(task.id, range.startDate, range.dueDate);
      return range;
    },
    onError: (error) => toast.error(errorMessage(error, t)),
    onSuccess: (range, variables) => {
      // Scope creep nói ra **lúc kéo**, không để người dùng phát hiện ở
      // burndown tuần sau: cam kết đã chốt lúc bắt đầu sprint (APM-AGILE-a).
      if (isScopeCreep(resolvedSprint?.status)) {
        toast.warning(t('scheduleDrawer.scopeCreep', { key: variables.task.key }));
      } else {
        toast.success(
          t('scheduleDrawer.scheduled', {
            key: variables.task.key,
            from: range.startDate,
            to: range.dueDate,
          }),
        );
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: boardKeys.board(projectId, sprintId) }),
        // Tiền tố, không phải khoá đầy đủ — APM-MAINT-h. Khoá backlog nay mang
        // thêm kỳ, nên `boardKeys.backlog(projectId)` là `[…, null]`: một khoá
        // **khác**, không phải tiền tố của khoá đang gắn, và React Query sẽ
        // không làm mới gì cả. Cùng cạm bẫy đã cắn ở APM-GANTT-p.
        queryClient.invalidateQueries({ queryKey: ['backlog', projectId], exact: false }),
        queryClient.invalidateQueries({ queryKey: ganttKeys.view(projectId) }),
      ]);
    },
  });

  /**
   * Nút "đưa vào sprint" của một dòng: vào sprint, **chưa** đặt ngày.
   *
   * Bịa một ngày cho thao tác bấm nút là quyết định thay người dùng — họ chưa
   * nói lúc nào cả. Kéo lên Gantt mới là lúc họ nói.
   */
  const toSprint = useMutation({
    mutationFn: (task: TaskView) => boardApi.moveToSprint(task.id, resolvedSprintId!),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSuccess: (_result, task) => {
      if (isScopeCreep(resolvedSprint?.status)) {
        toast.warning(t('scheduleDrawer.scopeCreep', { key: task.key }));
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: boardKeys.board(projectId, sprintId) }),
        // Tiền tố, không phải khoá đầy đủ — APM-MAINT-h. Khoá backlog nay mang
        // thêm kỳ, nên `boardKeys.backlog(projectId)` là `[…, null]`: một khoá
        // **khác**, không phải tiền tố của khoá đang gắn, và React Query sẽ
        // không làm mới gì cả. Cùng cạm bẫy đã cắn ở APM-GANTT-p.
        queryClient.invalidateQueries({ queryKey: ['backlog', projectId], exact: false }),
      ]);
    },
  });

  return (
    <div className="flex flex-col gap-2">
      {/*
        Việc lệch tiến độ đứng **trên cùng** — APM-AI-z.

        Đây là tab người ta mở để sửa lịch, nên một việc đã trễ phải đập vào mắt
        trước bảng việc chứ không nằm dưới nó. Dải này tự ẩn khi không có gì
        lệch: một dải "hôm nay mọi thứ đúng hạn" là thứ người ta học cách không
        nhìn trong một tuần, rồi không nhìn cả khi nó đổi màu.
      */}
      <DriftBanner
        projectId={projectId}
        canAssign={canAssign}
        canSchedule={canEditColumns}
        onOpenTask={onOpenedTaskChange}
      />

      {/*
        Chọn sprint **trước**, rồi mới tới cách nhìn.
        
        Thứ tự này là thứ tự câu hỏi: "hai tuần nào" đứng trước "vẽ kiểu gì".
        Đặt bộ chọn sau bộ chuyển cách nhìn thì người dùng chọn cách vẽ cho một
        tập dữ liệu họ chưa biết là tập nào.
        
        Dự án Waterfall không có sprint, nên hàng này biến mất hẳn — ở đó câu
        hỏi thứ nhất không tồn tại.

        ⚠️ Điều kiện phải hỏi "có gì để chọn không", không phải "có sprint
        không" — APM-MAINT-e. Dự án bảo trì cũng **không có sprint nào**, nên
        điều kiện cũ khoá luôn cả bộ chọn kỳ: nó được dựng, được truyền đủ
        prop, và không bao giờ hiện ra. Đúng lỗi vừa báo.
      */}
      {/*
        Mẫu định kỳ không thuộc kỳ hay sprint nào, nên ở cách nhìn Định kỳ bộ
        chọn bị **khoá** — không ẩn (người dùng đổi 2026-09-28). Ẩn đi thì dải
        Board / Gantt / Danh sách / Định kỳ bên dưới nhảy lên một hàng mỗi lần
        bấm sang Định kỳ, và nút người ta vừa bấm chạy khỏi con trỏ. `title`
        nói vì sao nó xám: một nút khoá không lời giải thích trông như hỏng.
      */}
      {activePeriod !== null || (!waterfall && (backlog.data?.sprints.length ?? 0) > 0) ? (
        <div
          className="flex flex-wrap items-center gap-3"
          title={view === 'recurring' ? t('recurrence.pickersLocked') : undefined}
        >
          {activePeriod !== null ? (
            /*
              Kỳ **thay chỗ** bộ chọn sprint, không đứng cạnh — APM-MAINT-e. Dự
              án bảo trì không có sprint nào, nên `SprintPicker` vốn đã tự ẩn
              (`sprints.length === 0`); đặt hai thứ cạnh nhau chỉ tạo một khoảng
              trống ở đúng chỗ người dùng nhìn vào để biết mình đang xem gì.
            */
            <PeriodPicker
              value={activePeriod}
              onChange={onPeriodChange}
              disabled={view === 'recurring'}
            />
          ) : null}

          <SprintPicker
            sprints={backlog.data?.sprints ?? []}
            value={viewAll && !waterfall ? ALL_SPRINTS : resolvedSprintId}
            onChange={onSprintChange}
            // Board không có "cả dự án" — xem docblock của `SprintPicker`.
            allowAll={view !== 'board'}
            id="tasks-sprint"
            disabled={view === 'recurring'}
          />

          {resolvedSprint ? (
            <span
              className={cn(
                'text-xs tabular-nums text-muted-foreground',
                view === 'recurring' && 'opacity-50',
              )}
            >
              {displayDateRange(resolvedSprint.startDate, resolvedSprint.endDate)}
            </span>
          ) : null}
        </div>
      ) : null}

      {/*
        Chưa chọn sprint thì **chưa có gì để vẽ** — APM-BOARD-b.

        Xảy ra khi dự án không có sprint nào đang chạy: server không tự chọn
        được, và một board gộp cột của mọi sprint là thứ không đọc nổi. Nói ra
        và dừng lại, thay vì vẽ ra một bức tranh sai.
      */}
      {/*
        `autoPicking` là một khung hình, không phải một trạng thái người dùng
        đứng lại trong đó — vẽ `EmptyState` ở đây thì câu "Chọn một sprint"
        nhoáng lên rồi biến mất ở mọi lượt mở tab, và một câu hỏi nhấp nháy đọc
        như một lỗi. `EmptyState` giữ lại làm lưới an toàn: nó chỉ còn hiện khi
        không tự chọn được, tức server trả `sprint: null` cho chính id vừa đặt.
      */}
      {autoPicking ? (
        <Skeleton className="h-64 w-full" />
      ) : needsSprint ? (
        <EmptyState title={t('tasks.pickSprintTitle')} description={t('tasks.pickSprintHelp')} />
      ) : (
        <>
          {/*
            Điện thoại: cách nhìn và nút hành động **chung một hàng** — người
            dùng yêu cầu 2026-10-07. Để nguyên nhãn thì "Sửa nhanh", "Việc
            mới" rớt xuống hàng hai và đầu tab tốn thêm 44px trước khi thấy
            việc nào. Nên ở `max-sm` các nút chỉ còn icon (`ICON_ONLY_ON_PHONE`)
            — nhãn vẫn còn cho trình đọc màn hình, và `title` khi nhấn giữ.
            Còn dải cách nhìn thì ngược lại: bỏ icon, giữ chữ — ba chữ ngắn tự
            nói đủ, và 60px dành ra là chỗ cho nút Sửa của Gantt khỏi rớt hàng.
          */}
          <div className="flex flex-wrap items-center gap-1.5">
            <Tabs value={view} onValueChange={(value) => onViewChange(value as TasksViewMode)}>
              {/* `data-tour` — neo của tour AI (APM-TOUR-ai): có mặt với mọi vai trò. */}
              <TabsList variant="glass" data-tour="tasks-views">
                {TASK_VIEWS.filter((value) => value !== 'recurring' || maintenance).map((value) => {
                  const Icon = ICONS[value];
                  return (
                    <TabsTrigger key={value} value={value} className="max-sm:px-2.5">
                      <Icon aria-hidden className="mr-1.5 size-3.5 max-sm:hidden" />
                      {t(`tasks.view.${value}`)}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>

            {canEditTasks ? (
              <>
                {/*
                  "Sửa nhanh" — popup gom các thao tác AI trên diện rộng của
                  dự án, không gắn với một task đang mở — APM-UX-ai6.

                  Trước đây nút này chỉ mở "Sửa hàng loạt bằng lời"; nay là một
                  menu, vì "Suy ra phụ thuộc còn thiếu" đã dọn khỏi chân trang
                  Gantt về đây — cùng nhóm, khác chỗ đứng chứ không khác việc.
                  Còn nút riêng nào nữa thì thêm mục vào menu này, không thêm
                  nút mới cạnh nó.

                  **Luôn** hiện, kể cả khi chưa có key (2026-09-25 — trước đây
                  ẩn hẳn, và người mới không biết tính năng tồn tại). Chưa có
                  key thì mục AI mở hộp "Cần key AI" chỉ chỗ nhập key và cách
                  làm tay (chọn nhiều việc rồi đổi; kéo thả nối phụ thuộc).

                  Icon AI đứng trước nhãn, cỡ và biến thể khớp ba nút cạnh nó
                  (`PlanDraftsButton`, "Việc mới", "Sửa biểu đồ") — mỗi nút
                  một màu hover riêng (`toolbar-button-hover.ts`) để phân
                  biệt bằng mắt trước cả khi đọc chữ.
                */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className={cn('ml-auto', ICON_ONLY_ON_PHONE, BULK_EDIT_HOVER)}
                      title={t('bulkEdit.open')}
                      data-tour="tasks-ai-menu"
                    >
                      <Sparkles aria-hidden />
                      <span className="max-sm:sr-only">{t('bulkEdit.open')}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      onSelect={() =>
                        keyGate.run('bulk_edit', t('bulkEdit.title'), () => setBulkEditing(true))
                      }
                    >
                      <MessageSquareText aria-hidden className="size-4" />
                      {t('bulkEdit.title')}
                    </DropdownMenuItem>
                    {/*
                      Kế hoạch nháp — vốn là nút riêng `PlanDraftsButton`
                      (APM-ROLE-w), nay là một mục ở đây cạnh hai mục kia,
                      cùng lý do đã ghi cho "Suy ra phụ thuộc còn thiếu".
                    */}
                    <DropdownMenuItem
                      onSelect={() =>
                        keyGate.run('generate_plan', t('plan.openDrafts'), () =>
                          setPlanDraftsOpen(true),
                        )
                      }
                    >
                      <Bot aria-hidden className="size-4" />
                      {t('plan.openDrafts')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() =>
                        keyGate.run('infer_dependencies', t('inferDeps.run'), () =>
                          setInferDepsOpen(true),
                        )
                      }
                    >
                      <Waypoints aria-hidden className="size-4" />
                      {t('inferDeps.run')}
                    </DropdownMenuItem>
                    {/*
                      "Xử lý task chậm tiến độ" — trước là nút "Cân bằng" riêng,
                      người dùng chuyển vào đây 2026-09-23. Luôn có mặt, kể cả
                      khi không có việc nào trễ: một nút lúc có lúc không là
                      thứ người ta tưởng đã mất. Mở hộp không tốn gì — AI chỉ
                      chạy khi bấm "Phân tích" bên trong.
                    */}
                    <DropdownMenuItem onSelect={() => setRebalancing(true)}>
                      <Scale aria-hidden className="size-4" />
                      {t('rebalanceAi.open')}
                    </DropdownMenuItem>
                    {/*
                      "Xếp lịch bằng AI" — **chỉ ở điện thoại**, nút riêng của nó
                      bên dưới ẩn đi (`max-sm:hidden`). Ở 390px nút thứ tư rớt
                      xuống một hàng riêng chỉ để chứa một icon (2026-10-07).
                      Cùng điều kiện với nút: chỉ Gantt, chỉ khi có gì để xếp.
                    */}
                    {view === 'gantt' && worthRescheduling ? (
                      <DropdownMenuItem
                        className="sm:hidden"
                        disabled={reschedule.ask.isPending}
                        onSelect={() =>
                          aiConfirm.confirm('reschedule', t('reschedule.ask'), () =>
                            reschedule.ask.mutate(),
                          )
                        }
                      >
                        <Wand2 aria-hidden className="size-4" />
                        {t('reschedule.ask')}
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  size="sm"
                  variant="outline"
                  className={cn(ICON_ONLY_ON_PHONE, NEW_TASK_HOVER)}
                  title={t('taskForm.newTask')}
                  data-tour="tasks-new"
                  onClick={() => setCreating(true)}
                >
                  <Plus aria-hidden />
                  <span className="max-sm:sr-only">{t('taskForm.newTask')}</span>
                </Button>
              </>
            ) : null}

            {/*
              Chế độ **sửa Gantt** — APM-GANTT-j.

              Biểu đồ có ba thao tác sửa cùng chỗ với thao tác đọc: kéo thanh để
              dời lịch, kéo hàng để xếp lại thứ tự, và kéo đầu nối để tạo phụ
              thuộc. Bật sẵn cả ba nghĩa là mỗi lần người dùng định **bấm** vào
              một việc để xem chi tiết, họ đang ở cách một cú trượt tay so với
              việc dời lịch của nó.

              Nên mặc định biểu đồ chỉ để đọc: bấm vào việc thì mở chi tiết, và
              không có đầu nối lẫn nút xoá phụ thuộc nào hiện ra. Muốn sửa thì
              bật nút này — một cú bấm có chủ đích, đổi lấy việc không bao giờ
              sửa nhầm.

              ⚠️ Hỏi `canEditColumns` (`project:update`), **không** `canEditTasks`
              — APM-CHANGE-b. Ba thao tác sau cái nút này đều là **lập kế hoạch**,
              và lập kế hoạch là thẩm quyền của PM. `task:update` không phân biệt
              được: nó là `own` với CONTRIBUTOR, nên ai cũng dời được ngày việc
              của mình — mà một việc dời ngày là kế hoạch của cả dự án dời theo,
              qua đường găng và qua mọi việc phụ thuộc nó.

              Người không phải PM thì **không thấy nút**, chứ không phải thấy mà
              bấm vào bị từ chối: một nút chỉ để nói "bạn không được" là một nút
              không nên có. Muốn đổi lịch thì gửi yêu cầu thay đổi — cửa vốn đã
              có, ở tab Thay đổi.
            */}
            {view === 'gantt' && canEditColumns ? (
              <Button
                size="sm"
                variant={ganttEditing ? 'default' : 'outline'}
                className={cn(ICON_ONLY_ON_PHONE, ganttEditing ? undefined : GANTT_EDIT_HOVER)}
                title={ganttEditing ? t('gantt.editDone') : t('gantt.edit')}
                aria-pressed={ganttEditing}
                onClick={() => setGanttEditing((on) => !on)}
              >
                <Pencil aria-hidden />
                <span className="max-sm:sr-only">
                  {/*
                  Nhãn nói **việc bấm vào sẽ làm gì**, không nói trạng thái đang
                  ở: "Đang sửa" mô tả hiện tại, nên người dùng phải tự suy ra
                  bấm vào thì được gì. "Xong" thì nói thẳng.
                */}
                  {ganttEditing ? t('gantt.editDone') : t('gantt.edit')}
                </span>
              </Button>
            ) : null}

            {/*
              "Xếp lịch bằng AI" đứng **bên phải** "Việc mới" — APM-1313b.

              ⚠️ Vẫn chỉ hiện khi có gì để xếp, đúng luật cũ của
              `ReschedulePanel`: một nút gọi AI luôn sáng trên mọi dự án là lời
              mời tiêu tiền vào câu hỏi không ai hỏi. Và chỉ ở cách nhìn Gantt,
              vì bảng duyệt từng nước đi hiện ở đó — nút mà kết quả rơi vào một
              màn hình khác thì bấm xong không biết nhìn đâu.
            */}
            {view === 'gantt' && worthRescheduling ? (
              <Button
                size="sm"
                variant="outline"
                // Có menu "Sửa nhanh" thì ở điện thoại nút này nằm trong menu.
                className={cn(ICON_ONLY_ON_PHONE, canEditTasks ? 'max-sm:hidden' : 'ml-auto')}
                title={t('reschedule.ask')}
                disabled={reschedule.ask.isPending}
                onClick={() =>
                  aiConfirm.confirm('reschedule', t('reschedule.ask'), () =>
                    reschedule.ask.mutate(),
                  )
                }
              >
                <Wand2 aria-hidden />
                <span className="max-sm:sr-only">
                  {reschedule.ask.isPending ? t('reschedule.asking') : t('reschedule.ask')}
                </span>
              </Button>
            ) : null}
            {aiConfirm.dialog}
          </div>

          {/*
            Hộp "Cân bằng" — phương án của hệ thống (không cần AI) và phân tích
            của AI cho một việc đang trễ hạn. Xem `RebalanceAiSection`.
          */}
          {rebalancing ? (
            <Dialog label={t('rebalanceAi.title')} size="lg" onClose={() => setRebalancing(false)}>
              <RebalanceAiSection projectId={projectId} tasks={driftedTasks} defaultOpen />
            </Dialog>
          ) : null}

          {keyGate.dialog}

          {bulkEditing ? (
            <BulkEditDialog projectId={projectId} onClose={() => setBulkEditing(false)} />
          ) : null}

          {planDraftsOpen ? (
            <PlanDraftsDialog projectId={projectId} onClose={() => setPlanDraftsOpen(false)} />
          ) : null}

          {inferDepsOpen ? (
            <InferDependenciesDialog
              projectId={projectId}
              onClose={() => setInferDepsOpen(false)}
            />
          ) : null}

          {creating ? (
            <TaskFormDialog
              projectId={projectId}
              canApprove={canApproveSuggestions}
              canAssign={canAssign}
              onClose={() => setCreating(false)}
            />
          ) : null}

          {/*
            Thanh lọc dùng chung, đứng **trên** cả ba cách nhìn — APM-BOARD-h.

            Dựng cả khi board chưa về: các trục của nó (người, loại việc) suy từ
            dữ liệu board, và trong lúc chờ thì hàng nút tự rút gọn — xem
            `BoardFilterBar`. Ẩn rồi hiện lại thì cả bố cục nhảy một nhịp ở mỗi
            lượt mở tab.
          */}
          {board.data && view !== 'recurring' ? (
            <BoardFilterBar board={board.data} value={filter} onChange={setFilter} />
          ) : null}

          {view === 'board' ? (
            <Board
              projectId={projectId}
              // Waterfall không có sprint nào, nên board của nó là "mọi task".
              all={viewAll}
              {...(viewAll || askedSprintId === undefined ? {} : { sprintId: askedSprintId })}
              canEditColumns={canEditColumns}
              canManageColumns={canManageColumns}
              canEditTasks={canEditTasks}
              // Kéo thả thẻ = lập kế hoạch ⇒ chỉ PM (APM-CHANGE-b).
              canSchedule={canEditColumns}
              // Bấm tên ở chân thẻ để giao lại việc ⇒ `task:assign`, như bảng Danh sách.
              canAssign={canAssign}
              period={activePeriod}
              filter={filter}
              openedTaskId={openedTaskId}
              onOpenedTaskChange={onOpenedTaskChange}
            />
          ) : view === 'gantt' ? (
            /*
              Gantt cũng theo sprint — ba cách nhìn, một tập dữ liệu. Đường găng
              vì thế tính trong phạm vi ấy: "chuỗi việc nào quyết định sprint
              này có kịp không" mới là câu hỏi của người đang mở sprint. Việc
              chưa xếp lịch không có thanh (chưa có ngày) — nó ở ngăn kéo bên
              dưới, để kéo thẳng lên lịch.
            */
            <GanttPanel
              aiReschedule={reschedule}
              projectId={projectId}
              deadline={deadline}
              canEdit={canEditTasks}
              chartEditable={canEditColumns && ganttEditing}
              canApprove={canApproveSuggestions}
              period={activePeriod}
              filter={filter}
              {...(resolvedSprintId === null ? {} : { sprintId: resolvedSprintId })}
              {...(canEditColumns && ganttEditing
                ? {
                    // Kéo việc từ ngăn kéo lên lịch cũng là sửa — APM-GANTT-j,
                    // và nó vào sprint nên là lập kế hoạch — APM-CHANGE-b.
                    onDropTask: (taskId: string, startDate: string) => {
                      void boardApi
                        .task(taskId)
                        .then((task) => schedule.mutate({ task, startDate }))
                        .catch((error) => toast.error(errorMessage(error, t)));
                    },
                  }
                : {})}
            />
          ) : view === 'recurring' ? (
            <>
              <RecurrencePanel
                projectId={projectId}
                canManage={canEditColumns}
                onOpenTask={onOpenedTaskChange}
              />
              {/* Bấm một lần trong lịch sử ⇒ mở đúng task ấy, như ở Danh sách. */}
              {openedTaskId !== null ? (
                <TaskPopup
                  projectId={projectId}
                  taskId={openedTaskId}
                  canEdit={canEditTasks}
                  canAssign={canAssign}
                  canApprove={canApproveSuggestions}
                  onClose={() => onOpenedTaskChange(null)}
                />
              ) : null}
            </>
          ) : (
            <>
              <TaskListView
                projectId={projectId}
                period={activePeriod}
                filter={filter}
                all={viewAll}
                {...(viewAll || askedSprintId === undefined ? {} : { sprintId: askedSprintId })}
                canEdit={canEditTasks}
                canSchedule={canEditColumns}
                canAssign={canAssign}
                openedTaskId={openedTaskId}
                onOpen={onOpenedTaskChange}
              />

              {/*
                Popup của Danh sách — APM-TASK-i.

                Danh sách vẫn gọi `onOpen` từ lúc dựng, nhưng thứ đọc `taskId`
                trên URL lại là `Board`, và `Board` chỉ tồn tại ở chế độ Board.
                Nên ở đây bấm vào tên một việc chỉ đổi URL rồi im lặng.
              */}
              {openedTaskId !== null ? (
                <TaskPopup
                  projectId={projectId}
                  taskId={openedTaskId}
                  canEdit={canEditTasks}
                  canAssign={canAssign}
                  canApprove={canApproveSuggestions}
                  onClose={() => onOpenedTaskChange(null)}
                />
              ) : null}
            </>
          )}

          {/*
            Ngăn kéo dùng chung cho **cả ba** cách nhìn — APM-BOARD-c.

            Nó là nửa còn lại của việc lập lịch: nhìn chỗ trống rồi tìm việc lấp
            vào. Đặt nó thành một tab riêng thì người ta phải nhớ nửa kia trong
            đầu, và sẽ kéo bừa việc trên cùng danh sách.
          */}
          {/*
            ⚠️ **Không** hiện ở Board: cột Backlog ở đó đã chính là danh sách
            việc chưa xếp lịch, và hai bản sao của cùng một danh sách trên cùng
            một màn hình sẽ lệch nhau ngay khi một bên quên nạp lại.
          */}
          {waterfall || view === 'board' || view === 'recurring' ? null : (
            <ScheduleDrawer
              projectId={projectId}
              sprintName={resolvedSprint?.name ?? null}
              // Đưa một việc vào sprint là lập kế hoạch ⇒ `project:update`
              // (APM-CHANGE-b), cùng quyền mà `PATCH /tasks/:id/sprint` đòi.
              canSchedule={canEditColumns}
              onSendToSprint={(task) => toSprint.mutate(task)}
            />
          )}
        </>
      )}
    </div>
  );
}
